// src/App.jsx
//
// PERFORMANCE: Route-level code splitting
// ─────────────────────────────────────────────────────────────
// Previously every page (including Admin Dashboard, Checkout,
// Account, Login/Register, etc.) was bundled into the single
// main JS file and shipped to every visitor — even someone who
// only ever views the homepage. This was the dominant source of
// the "Reduce unused JavaScript" audit warning (273 KiB).
//
// React.lazy() + Suspense splits each page into its own chunk
// that Vite only downloads when that route is actually visited.
//
// HomePage stays as a normal (eager) import because:
//   1. It's the entry point for ~most traffic
//   2. Lazy-loading it would add a network round-trip BEFORE the
//      LCP hero image can even start downloading, which would
//      hurt LCP rather than help it.
//
// Layout (Header/Footer/Toaster/etc.) also stays eager since it
// renders on every page anyway.
// ─────────────────────────────────────────────────────────────

import React, { Suspense, lazy } from 'react';
import { Route, Routes, BrowserRouter as Router, useLocation } from 'react-router-dom';
import ScrollToTop from './components/ScrollToTop.jsx';
import Header from './components/Header.jsx';
import Footer from './components/Footer.jsx';

// Providers
import { CartProvider } from './context/CartContext.jsx';
import { AuthProvider } from './context/AuthContext.jsx';

// ─── Eager: homepage + layout-critical pieces ──────────────────
import HomePage from './pages/HomePage.jsx';
import FloatingWhatsApp from './components/FloatingWhatsApp.jsx';
import AILoader from './components/AILoader.jsx';
import { Toaster } from '@/components/ui/sonner';

// ─── Lazy: everything else ──────────────────────────────────────
const AboutPage          = lazy(() => import('./pages/AboutPage.jsx'));
const BlogPage            = lazy(() => import('./pages/BlogPage.jsx'));
const BlogPostPage        = lazy(() => import('./pages/BlogPostPage.jsx'));
const TagPage              = lazy(() => import('./pages/TagPage.jsx'));
const ContactPage          = lazy(() => import('./pages/ContactPage.jsx'));
const FAQPage              = lazy(() => import('./pages/FAQPage.jsx'));
const DeliveryInfoPage    = lazy(() => import('./pages/DeliveryInfoPage.jsx'));
const TermsPage            = lazy(() => import('./pages/TermsPage.jsx'));
const PrivacyPage          = lazy(() => import('./pages/PrivacyPage.jsx'));
const ShippingPage        = lazy(() => import('./pages/ShippingPage.jsx'));
const ReturnsPage          = lazy(() => import('./pages/ReturnsPage.jsx'));
const POPIAPage            = lazy(() => import('./pages/POPIAPage.jsx'));
const NotFoundPage        = lazy(() => import('./pages/NotFoundPage.jsx'));
const ProductDetailPage    = lazy(() => import('./pages/ProductDetailPage.jsx'));
const ShopPage            = lazy(() => import('./pages/ShopPage.jsx'));
const CartPage            = lazy(() => import('./pages/CartPage.jsx'));
const CheckoutPage        = lazy(() => import('./pages/CheckoutPage.jsx'));
const PaymentSuccessPage  = lazy(() => import('./pages/PaymentSuccessPage.jsx'));
const PaymentCancelPage    = lazy(() => import('./pages/PaymentCancelPage.jsx'));
const LoginPage            = lazy(() => import('./pages/LoginPage.jsx'));
const RegisterPage        = lazy(() => import('./pages/RegisterPage.jsx'));
const AccountPage          = lazy(() => import('./pages/AccountPage.jsx'));

// Admin pages — these are large (icons, tables, managers) and
// should NEVER be in the public bundle.
const AdminLogin          = lazy(() => import('./pages/AdminLogin'));
const AdminDashboard      = lazy(() => import('./pages/AdminDashboard'));

// ─── Route-change loading fallback ──────────────────────────────
// Minimal, non-shifting placeholder. Returning `null` would cause
// a blank flash; this keeps a stable height with a subtle spinner
// so CLS stays at 0 even while a chunk downloads.
function RouteFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[hsl(var(--primary))]" />
    </div>
  );
}

// Wrapper component to conditionally render layout
function Layout({ children }) {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  return (
    <div className="flex flex-col min-h-screen bg-[hsl(var(--background))] font-sans selection:bg-[hsl(var(--accent))] selection:text-white">
      {!isAdminRoute && <Header />}
      <main className="flex-1 flex flex-col w-full relative z-0">
        {children}
      </main>
      {!isAdminRoute && <Footer />}
      {!isAdminRoute && <FloatingWhatsApp />}
      {!isAdminRoute && <AILoader />}
      <Toaster position="bottom-right" richColors />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <CartProvider>
          <ScrollToTop />

          <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* Core */}
              <Route path="/" element={<Layout><HomePage /></Layout>} />
              <Route path="/about" element={<Layout><AboutPage /></Layout>} />
              <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
              <Route path="/faq" element={<Layout><FAQPage /></Layout>} />

              {/* Shop & Cart */}
              <Route path="/shop" element={<Layout><ShopPage /></Layout>} />
              <Route path="/shop/:slug" element={<Layout><ProductDetailPage /></Layout>} />
              <Route path="/cart" element={<Layout><CartPage /></Layout>} />
              <Route path="/checkout" element={<Layout><CheckoutPage /></Layout>} />
              <Route path="/payment-success" element={<Layout><PaymentSuccessPage /></Layout>} />
              <Route path="/payment-cancel" element={<Layout><PaymentCancelPage /></Layout>} />

              {/* Auth */}
              <Route path="/login" element={<Layout><LoginPage /></Layout>} />
              <Route path="/register" element={<Layout><RegisterPage /></Layout>} />
              <Route path="/account" element={<Layout><AccountPage /></Layout>} />

              {/* Blog */}
              <Route path="/blog" element={<Layout><BlogPage /></Layout>} />
              <Route path="/blog/:slug" element={<Layout><BlogPostPage /></Layout>} />
              <Route path="/blog/tag/:tag" element={<Layout><TagPage /></Layout>} />

              {/* Info */}
              <Route path="/delivery-info" element={<Layout><DeliveryInfoPage /></Layout>} />

              {/* Legal */}
              <Route path="/terms" element={<Layout><TermsPage /></Layout>} />
              <Route path="/privacy" element={<Layout><PrivacyPage /></Layout>} />
              <Route path="/shipping" element={<Layout><ShippingPage /></Layout>} />
              <Route path="/returns" element={<Layout><ReturnsPage /></Layout>} />
              <Route path="/popia" element={<Layout><POPIAPage /></Layout>} />

              {/* Admin (no layout) */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminDashboard />} />

              {/* 404 */}
              <Route path="*" element={<Layout><NotFoundPage /></Layout>} />
            </Routes>
          </Suspense>
        </CartProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;